import { Box, Button, Typography, useTheme, Grid2, TextField, useMediaQuery, List, ListItem, ListItemIcon, ListItemText, IconButton, Stack, useColorScheme } from "@mui/material";
import StarsFilled from "./assets/StarsFilled";
import { useDraftPersona } from "../creatorOnBoarding/useDraftPersona";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { toast } from "react-toastify";
import { signInAnonymously } from "firebase/auth";
import { doc, setDoc, Timestamp } from "firebase/firestore";
import { auth, firestorage, firestore } from "../../utils/firebase";
import { ref, uploadBytes } from "firebase/storage";
import { USER_TYPE } from "../../config/const";
import { updatePersona } from "../../utils/api";
import LinkIcon from "../../svg/LinkIcon";
import DeleteIcon2 from "../../svg/DeleteIcon2";
import FileIcon2 from "../../svg/FileIcon2";
import DropFilesIcon from "../../svg/DropFilesIcon";

const enableDevTools = process.env.REACT_APP_ENABLE_DEVTOOLS === 'true';

const CreatePersonaForm: React.FC = () => {
  const {
    draft,
    addBasicInfo,
    addLink,
    removeLink,
    addFile,
    removeFile,
    addErrors
  } = useDraftPersona()
  const formRef = useRef<HTMLDivElement>(null)

  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [linkInput, setLinkInput] = useState('')
  const [linkError, setLinkError] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)
  const navigate = useNavigate()
  const [isCreating, setIsCreating] = useState(false)
  const theme = useTheme();
  const [firstNameError, setFirstNameError] = useState<string | null>(null)
  const [lastNameError, setLastNameError] = useState<string | null>(null)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [trainingMaterialError, setTrainingMaterialError] = useState<string | null>(null)

  const { checkUserExistWithUsername } = useAuth();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const { mode, systemMode } = useColorScheme();
  const isLightMode = ( mode === 'light' || (mode === 'system' && systemMode === 'light'));

  useEffect(() => {
    if (formRef.current) {
      formRef.current.scrollTop = formRef.current.scrollHeight
    }
  }, [draft.links.length, draft.files.length])

  function handleFileBrowse() {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '*/*'
    input.onchange = () => {
      const file = input.files?.[0]
      if (!file) return
      addFile(file)
    }
    input.click()
  }

  const confirmAddLink = () => {
    const raw = linkInput.trim();
    if (!raw) {
      setLinkError('URL is required');
      return;
    }

    const candidate = raw.startsWith('http') ? raw : `https://${raw}`;

    let parsed: URL;
    try {
      parsed = new URL(candidate);
    } catch {
      setLinkError('Please enter a valid URL');
      return;
    }
    if (!parsed.hostname.includes('.')) {
      setLinkError('Please enter a valid web address');
      return;
    }

    addLink(parsed.toString());
    setLinkInput('');
    setLinkError(null);
    setLinkDialogOpen(false);
  };

  const handleCreate = async (e: React.MouseEvent) => {    
    e.preventDefault()

    // Validate First name
    if (!draft.firstName.trim()) {
      setFirstNameError('First Name is required')
      return
    }
    setFirstNameError(null)

    // Validate Last Name
    if (!draft.lastName.trim()) {
      setLastNameError('Last Name is required')
      return
    }
    setLastNameError(null)

    // Validate Email
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!draft.email.trim()) {
      setEmailError("Email is required");
      return;
    } else if (!emailPattern.test(draft.email.trim())) {
      setEmailError("Enter a valid email address");
      return;
    }

    setEmailError(null);


    // Validate training material
    if (draft.files.length === 0 && draft.links.length === 0) {
      setTrainingMaterialError('Please add at least one document or link')
      return
    }
    setTrainingMaterialError(null)

    setIsCreating(true)

    try {
      const { user } = await signInAnonymously(auth)

      const base = draft.firstName
        .trim()
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')

      let isValidName = false;
      let userName = base;

      while (!isValidName) {
        const suffix = Math.random().toString(36).substring(2, 6)
        userName = `${base}-${suffix}`;
        // check for uniqueness in db
        isValidName = !(await checkUserExistWithUsername(userName));
      }

      const uploadUserDocuments = async (userDocument: any, userId: string) => {
        const documentsPath: Record<any, any>[] = [];
        //check user document array has data
        if (userDocument && userDocument?.length >= 1) {
          const timeStamp = Timestamp.fromDate(new Date());
          // get uploaded documents upload status from firebase
          const documentsUploadStatus = await Promise.all<boolean>(
            userDocument.map(async (documentItem: any) => {
              const documentOrgName = documentItem?.name;
              const documentPath = `files/${userId}/${new Date().getTime()}_${documentItem?.name
                }`;
              const documentStorageRef = ref(firestorage, documentPath);
              try {
                await uploadBytes(documentStorageRef, documentItem?.file);
                documentsPath.push({
                  name: documentOrgName,
                  path: documentPath,
                  uploadedAt: timeStamp,
                });
                return true;
              } catch (err) {
                console.error(err);
                return false;
              }
            })
          );
          const rejectedIndex = documentsUploadStatus.findIndex(
            (item: boolean) => !item
          );
          if (rejectedIndex !== -1) {
            return false;
          }
        }
        return documentsPath;
      };

      let processedDocuments = draft.files.length > 0 ?
        await uploadUserDocuments(draft.files, user.uid) : [];

      // const googleDriveLinks = [];

      const docRef = doc(firestore, 'users', user.uid)
      const fullName = draft.firstName + ' ' + draft.lastName

      await setDoc(docRef, {
        fullName: fullName,
        temp_email: draft.email,
        shortBio: draft.description,
        userType: USER_TYPE.CREATOR,
        userName,
        normalizedUserName: userName.toLowerCase(),
        userWebPage: draft.links.map(link => ({
          web_link: link,
          disabled: false
        })),
        userDocument: processedDocuments,
        // google_drive_links: googleDriveLinks,
        createdAt: Timestamp.now(),
        verified: false,
        created_via_short_flow: true,
      }, { merge: true })

      const payload = {
        index_documents: draft.files.length > 0,
        index_webpage: draft.links.length > 0,
        index_twitter: false,
        index_yt: false,
        index_sitemap: false,
        index_gdrive: true,
      }

      let resp = await updatePersona(user.uid, payload)

      try {
        if (resp.ok) {
          let data = await resp.json()
          if (enableDevTools) console.log('[CreatePersonaForm] updatePersona response:', data)
          if (!data?.document_count) {
            setTrainingMaterialError('Could not process training material. Please try again.')
            let compiledErrors = {};
            for (let err of data.errors) {
              for (const [key, value] of Object.entries(err)) {
                compiledErrors[key] = value;
              }
            }
            addErrors(compiledErrors);
          } else {
            navigate(`/${userName}/chat`)
          }
        }
      } catch (err: any) {
        if (enableDevTools) console.error('[CreatePersonaForm] updatePersona response error:', err)
        toast.error( 'Something went wrong while creating your AI Persona. Please try again later.' )
      }

    } catch (err: any) {
      console.error('[CreatePersonaForm] updatePersona failed:', err.response?.data ?? err)
      toast.error( 'Something went wrong while creating your AI Persona. Please try again later.' )
      // toast.error(
      //   'Indexing error: ' +
      //   (err.response?.data?.message || err.message || 'please check console')
      // )
    } finally {
      setIsCreating(false)
    }
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const files = Array.from(e.dataTransfer.files)
    files.forEach(file => addFile(file))
  }

  const maxVisible = 2
  let linkCount = showAll ? draft.links.length : Math.min(draft.links.length, maxVisible)
  let remaining = showAll ? draft.files.length : maxVisible - linkCount
  let fileCount = showAll ? draft.files.length : Math.min(draft.files.length, remaining)
  const visibleLinks = draft.links.slice(0, linkCount)
  const visibleFiles = draft.files.slice(0, fileCount)
  const totalItems = draft.links.length + draft.files.length


  return (
    <Box
      sx={{
        maxWidth: "lg",
        width: "100%",
        px: {xs: 0, md: 5},
        py: {xs: 0, md: 4},
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 3,
          px: {xs: 2.5, md: 3},
          py: {xs: 3, md: 4},
          backgroundColor: theme => theme.palette.background["container-2"],
          borderRadius: {xs: 0, md: 1.5},
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            maxWidth: "620px",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <Typography variant="h5">
            Create Your AI Persona in minutes—no commitment
          </Typography>
          <Typography variant="body1" color="text.secondary">
            In minutes, you can see your AI persona answering questions and
            sharing your voice—without the full setup. Get a quick preview
            before deciding to launch.
          </Typography>
        </Box>

        <Grid2
          container
          spacing={2}
          sx={{
            width: "100%",
          }}
        >
          <Grid2 id="persona-form-first-name"
            size={{ xs: 6, md: 4}}
          >
            <Typography variant="body2" color="text.primary" sx={{ mb: 1.5 }}>
              First Name
            </Typography>
            <TextField
              fullWidth
              placeholder="David"
              value={draft.firstName}
              onChange={e => {
                addBasicInfo({ firstName: e.target.value })
                if (firstNameError) setFirstNameError(null)
              }}
              className="default marketing"
              slotProps={{
                input: {
                  sx: {
                    backgroundColor: isLightMode ? 'common.white' : "#343434",
                    color: 'text.primary',
                    width: '100%',
                    borderRadius: '10px',
                    "& fieldset": {
                        border: "2px solid #42424250",
                      },
                  }
                }
              }}
              error={!!firstNameError}
              helperText={firstNameError}
            />
          </Grid2>

          <Grid2 id="persona-form-last-name"
            size={{ xs: 6, md: 4}}
          >
            <Typography variant="body2" color="text.primary" sx={{ mb: 1.5 }}>
              Last Name
            </Typography>
            <TextField
              fullWidth
              placeholder="Mitchell"
              value={draft.lastName}
              onChange={e => {
                addBasicInfo({ lastName: e.target.value })
                if (lastNameError) setLastNameError(null)
              }}
              slotProps={{
                input: {
                  sx: {
                    backgroundColor: isLightMode ? 'common.white' : "#343434",
                    color: 'text.primary',
                    borderRadius: '10px',
                    "& fieldset": {
                      border: "2px solid #42424250",
                    },
                  }
                }
              }}
              error={!!lastNameError}
              helperText={lastNameError}
            />
          </Grid2>

          <Grid2 id="persona-form-email"
            size={{ xs: 12, md: 4}}
          >
            <Typography variant="body2" color="text.primary" sx={{ mb: 1.5 }}>
              Email Address
            </Typography>
            <TextField
              fullWidth
              placeholder="name@gmail.com"
              value={draft.email}
              onChange={e => {
                addBasicInfo({ email: e.target.value })
                if (emailError) setEmailError(null)
              }}
              slotProps={{
                input: {
                  sx: {
                    backgroundColor: isLightMode ? 'common.white' : "#343434",
                    color: 'text.primary',
                    borderRadius: '10px',
                    "& fieldset": {
                      border: "2px solid #42424250",
                    },
                  }
                }
              }}
              variant='outlined'
              error={!!emailError}
              helperText={emailError}
            />
          </Grid2>

          <Grid2 id="persona-form-description"
            size={{ xs: 12, md: 6}}
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="body2" color="text.primary" sx={{ mb: 1.5 }}>
              Persona Description
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              maxRows={8}
              placeholder="Example: Award winning fitness coach and nutritionist helping people reach their goals."
              value={draft.description}
              onChange={e => addBasicInfo({ description: e.target.value })}
              slotProps={{
                input: {
                  sx: {
                    backgroundColor: isLightMode ? 'common.white' : "#343434",
                    color: 'text.primary',
                    height: '100%',
                    borderRadius: '10px',
                      "& fieldset": {
                        border: "2px solid #42424250",
                      }
                    }
                  }
                }}
                variant='outlined'
              sx={{
                flex: 1,
                '& .MuiInputBase-root': {
                  height: '100%'
                }
              }}
            />
          </Grid2>

          <Grid2 id="persona-form-training-material"
            size={{ xs: 12, md: 6}}
          >
            <Typography variant="body2" color="text.primary">
              Training Material
            </Typography>
            <Box sx={{ mb: 1.5 }}>
              <List dense disablePadding sx={{ mb: 0 }}>
                {/* Show links */}
                {visibleLinks.length > 0 && (
                  <>
                    {visibleLinks.map((link, i) => (
                      <ListItem key={i} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <LinkIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={link}
                          slotProps={{
                            primary: {
                              color: !!((draft.errors || {})[link]) ? 'error.main' : 'text.primary',
                            }
                          }}
                        />
                        <IconButton size="small" onClick={() => removeLink(link)}>
                          <DeleteIcon2 fontSize="small" />
                        </IconButton>
                      </ListItem>
                    ))}
                  </>
                )}

                {/* Show files */}
                {visibleFiles.length > 0 && (
                  <>
                    {visibleFiles.map((doc, i) => (
                      <ListItem key={i} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <FileIcon2 fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={doc.name}
                          slotProps={{
                            primary: {
                              color: !!((draft.errors || {})[doc.name]) ? 'error.main' : 'text.primary',
                            }
                          }}
                        />
                        <IconButton size="small" onClick={() => removeFile(doc.name, i)}>
                          <DeleteIcon2 fontSize="small" />
                        </IconButton>
                      </ListItem>
                    ))}
                  </>
                )}
              </List>

              {/* See All button toggles full list */}
              {totalItems > maxVisible && (
                <Button size="small" sx={{ mt: 1 }} onClick={() => setShowAll(!showAll)}>
                  {showAll ? 'See less' : 'See all'}
                </Button>
              )}
            </Box>
            {trainingMaterialError && (
              <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                {trainingMaterialError}
              </Typography>
            )}
            { isXs ?
              <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                width='100%'
              >
                <Button
                  variant="outlined"
                  onClick={handleFileBrowse}
                  startIcon={<FileIcon2 color={theme.palette.text.primary}/>}
                  color='secondary'
                  sx={{flex: 1}}
                >
                  Add file
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setLinkDialogOpen(true)}
                  startIcon={<LinkIcon color={theme.palette.text.primary}/>}
                  color='secondary'
                  sx={{flex: 1}}
                >
                  Add web-link
                </Button>
              </Stack> :
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: '#42424280',
                  bgcolor: (theme) => theme.palette.background['paper-elevation-8'],
                  borderRadius: 3,
                  p: 2,
                  textAlign: 'center',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  minHeight: 0
                }}
                onDragOver={e => e.preventDefault()}
                onDrop={handleDrop}
              >
                <Typography variant="h7" color="text.primary" mb={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box sx={{display:'flex', flexDirection: 'row', gap: '5px', alignItems:'center', justifyContent: 'center'}}>
                    <DropFilesIcon color={theme.palette.text.primary}/>
                    <Typography>Drop files here</Typography>
                  </Box>
                </Typography>
                <Typography variant="body3" color="text.secondary" display="block" mb={1} >
                  or
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="center">
                  <Button
                    variant="outlined"
                    onClick={handleFileBrowse}
                    startIcon={<FileIcon2 color={theme.palette.text.primary}/>}
                    color='secondary'
                  >
                    Add file
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setLinkDialogOpen(true)}
                    startIcon={<LinkIcon color={theme.palette.text.primary}/>}
                    color='secondary'
                  >
                    Add web-link
                  </Button>
                </Stack>
              </Box>
            }
          </Grid2>

        </Grid2>

        <Button
          variant="contained"
          size="large"
          onClick={handleCreate}
          sx={{
            width: {xs: "100%", md: "fit-content"},
          }}
          startIcon={<StarsFilled
            color={theme.palette.primary.contrastText}
          />}
        >
          Create my AI persona
        </Button>
      </Box>
    </Box>
  );
};

export default CreatePersonaForm;
